import React from 'react';
import SkeletonLoading, {
  SkeletonAnimation,
  SkeletonModal,
  ModalSkeletonVariant,
} from '../../../components/SkeletonLoading';
import { useModalSkeleton } from '../../../components/SkeletonLoading/ModalSkeletonContext';
import './styles.scss';

interface LoadingProps {
  loading?: boolean;
  backgroundColor?: string;
  spinnerColor?: string;
  useSkeleton?: boolean;
  skeletonType?: "table" | "card" | "form" | "overlay";
  isOverlay?: boolean;
  /** Formato do conteúdo do modal espelhado pelo skeleton. */
  skeletonVariant?: ModalSkeletonVariant;
  skeletonFields?: number;
  skeletonColumns?: number;
  skeletonRows?: number;
  skeletonFooterButtons?: number;
  skeletonAvatar?: boolean;
  skeletonTitle?: boolean;
  skeletonAnimation?: SkeletonAnimation;
  /** Escape hatch: força o skeleton de página mesmo dentro de um modal. */
  forcePageSkeleton?: boolean;
}

const Loading: React.FC<LoadingProps> = ({
  loading = false,
  skeletonType = "table",
  isOverlay = false,
  skeletonVariant,
  skeletonFields,
  skeletonColumns,
  skeletonRows,
  skeletonFooterButtons,
  skeletonAvatar,
  skeletonTitle,
  skeletonAnimation,
  forcePageSkeleton = false,
}: LoadingProps) => {
  const { insideModal } = useModalSkeleton();

  if (!loading) return null;

  if (isOverlay || skeletonType === "overlay") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs">
        <div className="bg-white px-8 py-6 rounded-2xl shadow-2xl flex flex-col items-center gap-3">
          <div className="w-9 h-9 border-4 border-[#003366] border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm font-semibold text-[#003366]">Aguarde...</span>
        </div>
      </div>
    );
  }

  // Dentro de um modal o skeleton vira overlay absoluto: não ocupa espaço no
  // layout, então o modal mantém a altura do conteúdo real do início ao fim.
  if (insideModal && !forcePageSkeleton) {
    return (
      <div className="skeleton-modal-overlay" aria-busy="true">
        <SkeletonModal
          variant={skeletonVariant}
          fields={skeletonFields}
          columns={skeletonColumns}
          rows={skeletonRows}
          footerButtons={skeletonFooterButtons}
          showAvatar={skeletonAvatar}
          showTitle={skeletonTitle}
          animation={skeletonAnimation}
        />
      </div>
    );
  }

  return (
    <div className="w-full p-2">
      <SkeletonLoading type={skeletonType} rows={6} />
    </div>
  );
};

export default Loading;
